import styles from './W_SignContainer.module.css';
import W_TitleSign from '../W_TitleSign/W_TitleSign';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';
import { useAuth } from '@/contexts/AuthContext';
import { authApi } from '@/services/authApi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import W_AuthForm from '../W_AuthForm/W_AuthForm';

interface W_SignContainerProps {
  onError?: (error: string | null) => void;
  onSuccess?: (message: string | null) => void;
}

export default function W_SignContainer({
  onError,
  onSuccess,
}: W_SignContainerProps) {
  const { language } = useLanguage();
  const {
    login,
    register,
    signInWithProvider,
    isLoading: authLoading,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [variant, setVariant] = useState<'login' | 'register'>('register');
  const [error, setError] = useState<string | null>(null);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendDone, setResendDone] = useState(false);
  const referralToken =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('referralToken') ||
        undefined
      : undefined;

  useEffect(() => {
    const mode = new URLSearchParams(location.search).get('mode');
    setVariant(mode === 'login' ? 'login' : 'register');
  }, [location.search]);

  const handleResendVerification = async (email: string) => {
    setResendLoading(true);
    setResendDone(false);
    try {
      await authApi.resendVerifyEmail(email);
      setResendDone(true);
      onSuccess?.(getTranslation(language, 'auth.resendVerificationSent'));
    } catch {
      onError?.(getTranslation(language, 'auth.verifyEmailError'));
    } finally {
      setResendLoading(false);
    }
  };

  const handleAuth = async (
    email: string,
    password: string,
    name?: string,
    acceptTerms?: boolean
  ) => {
    setError(null);
    setEmailNotVerified(false);
    setResendDone(false);
    onError?.(null);
    onSuccess?.(null);

    try {
      let result;

      if (variant === 'login') {
        result = await login({ email, password });
      } else {
        result = await register({ email, password, referralToken });
      }

      if (result.success) {
        const successMessage =
          variant === 'login'
            ? getTranslation(language, 'auth.loginSuccess')
            : getTranslation(language, 'auth.registerEmailSent');
        onSuccess?.(successMessage);
        if (variant === 'login') {
          setTimeout(() => navigate('/'), 1000);
        }
      } else if ('emailNotVerified' in result && result.emailNotVerified) {
        setEmailNotVerified(true);
        setUnverifiedEmail(
          'email' in result && typeof result.email === 'string'
            ? result.email
            : email
        );
        onError?.(null);
      } else {
        const errorMessage = result.error || 'Произошла ошибка';
        setError(errorMessage);
        onError?.(errorMessage);
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Произошла неизвестная ошибка';
      setError(errorMessage);
      onError?.(errorMessage);
    }
  };

  const handleSocialAuth = async (provider: string) => {
    setError(null);
    onError?.(null);
    onSuccess?.(null);

    try {
      const result = await signInWithProvider(provider);

      if (result.success) {
        onSuccess?.(getTranslation(language, 'auth.socialAuthSuccess'));
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        const errorMessage = result.error || 'Ошибка входа через соцсеть';
        setError(errorMessage);
        onError?.(errorMessage);
      }
    } catch (error) {
      const errorMessage = 'Произошла ошибка при входе через соцсеть';
      setError(errorMessage);
      onError?.(errorMessage);
    }
  };

  return (
    <div className={styles.wrapper}>
      <W_TitleSign
        variant={variant}
        onSwitchVariant={() => {
          setVariant(variant === 'login' ? 'register' : 'login');
          setError(null);
          setEmailNotVerified(false);
          setResendDone(false);
          onError?.(null);
          onSuccess?.(null);
        }}
      />
      <W_AuthForm
        key={language}
        variant={variant}
        onSubmit={handleAuth}
        onSocialAuth={handleSocialAuth}
        onError={onError}
        isLoading={authLoading}
        error={error || undefined}
        emailNotVerified={emailNotVerified}
        unverifiedEmail={unverifiedEmail}
        resendLoading={resendLoading}
        resendDone={resendDone}
        onResendVerification={handleResendVerification}
      />
    </div>
  );
}
